import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import dayjs from "dayjs";
import { useAppSelector } from "../../../../app/hooks";
import "./_charts.scss";

const convertDate = (yyyymmdd) => {
  // accept YYYYMMDD (and still handle YYYYMMDDHHmmss if it shows up later)
  const d = dayjs(String(yyyymmdd), ["YYYYMMDD", "YYYYMMDDHHmmss"]);
  return d.isValid() ? d.toDate().getTime() : NaN; // Apex likes numeric timestamps
};

const getTradeChartData = (tradeData) => {
  if (!Array.isArray(tradeData)) return [];
  return tradeData
    .map(([dateStr, val]) => {
      const x = convertDate(dateStr);
      const yNum = Number(val);
      if (!isFinite(x) || !isFinite(yNum)) return null;
      const y = Math.round(yNum * 100) / 100; // keep as number, 2dp
      return [x, y];
    })
    .filter(Boolean);
};

const OverviewChart = () => {
  const { stats } = useAppSelector((state) => state.trade);
  const { firstTradeDate, accumDailyPnl } = stats || {};

  const [state, setState] = useState({
    series: [
      {
        name: "Total P&L",
        data: [],
      },
    ],
    options: {
      colors: ["#e1b382"],
      chart: {
        id: "area-datetime",
        type: "area",
        height: 250,
        zoom: {
          enabled: true,
          autoScaleYaxis: true,
          zoomedArea: {
            fill: { color: "#000", opacity: 0.4 },
            stroke: { color: "#e1b382", opacity: 0.4, width: 1 },
          },
        },
      },
      noData: {
        text: "No Data",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: -25,
        style: { color: "lightgray", fontSize: "32px" },
      },
      stroke: { curve: "smooth", colors: ["black"], width: 2 },
      dataLabels: { enabled: false },
      markers: { size: 0, style: "hollow", colors: ["#e1b382"] },
      xaxis: {
        type: "datetime",
        labels: {
          offsetX: 20,
          style: {
            colors: [],
            fontSize: "12px",
            fontFamily: "JetBrains Mono, monospace",
            fontWeight: "600",
          },
        },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          formatter: (value) => "$" + value.toLocaleString("en-US"),
          style: {
            colors: [],
            fontSize: "12px",
            fontFamily: "JetBrains Mono, monospace",
            fontWeight: "600",
          },
        },
      },
      tooltip: {
        x: { format: "MM/dd/yyyy" },
        followCursor: true,
        style: { fontSize: "12px", fontFamily: "monospace" },
      },
      grid: {
        borderColor: "#dadada",
        padding: { left: 10, right: 20 },
      },
      fill: {
        colors: ["#2d545e"],
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.9, opacityTo: 1 },
      },
    },
    selection: "ytd",
  });

  useEffect(() => {
    const data = getTradeChartData(accumDailyPnl);
    setState((prev) => ({
      ...prev,
      series: [{ ...prev.series[0], data }],
    }));
  }, [accumDailyPnl]);

  const updateData = (timeline) => {
    setState((s) => ({ ...s, selection: timeline }));

    const now = Date.now();
    const oneMonthAgo = new Date().setMonth(new Date().getMonth() - 1);
    const sixMonthsAgo = new Date().setMonth(new Date().getMonth() - 6);
    const oneYearAgo = new Date().setFullYear(new Date().getFullYear() - 1);
    const ytdStart = new Date(new Date().getFullYear(), 0, 1).getTime();

    const firstTs = firstTradeDate ? convertDate(firstTradeDate) : NaN;
    const lastTs =
      Array.isArray(accumDailyPnl) && accumDailyPnl.length
        ? convertDate(accumDailyPnl[accumDailyPnl.length - 1][0])
        : NaN;

    switch (timeline) {
      case "one_month":
        ApexCharts.exec("area-datetime", "zoomX", oneMonthAgo, now);
        break;
      case "six_months":
        ApexCharts.exec("area-datetime", "zoomX", sixMonthsAgo, now);
        break;
      case "one_year":
        ApexCharts.exec("area-datetime", "zoomX", oneYearAgo, now);
        break;
      case "ytd":
        ApexCharts.exec("area-datetime", "zoomX", ytdStart, now);
        break;
      case "all":
        if (isFinite(firstTs) && isFinite(lastTs)) {
          ApexCharts.exec("area-datetime", "zoomX", firstTs, lastTs);
        }
        break;
      default:
    }
  };

  return (
    <div id="chart">
      <div className="toolbar">
        <button
          id="one_month"
          onClick={() => updateData("one_month")}
          className={state.selection === "one_month" ? "active" : ""}
        >
          1M
        </button>
        &nbsp;
        <button
          id="six_months"
          onClick={() => updateData("six_months")}
          className={state.selection === "six_months" ? "active" : ""}
        >
          6M
        </button>
        &nbsp;
        <button
          id="one_year"
          onClick={() => updateData("one_year")}
          className={state.selection === "one_year" ? "active" : ""}
        >
          1Y
        </button>
        &nbsp;
        <button
          id="ytd"
          onClick={() => updateData("ytd")}
          className={state.selection === "ytd" ? "active" : ""}
        >
          YTD
        </button>
        &nbsp;
        <button
          id="all"
          onClick={() => updateData("all")}
          className={state.selection === "all" ? "active" : ""}
        >
          ALL
        </button>
      </div>

      <div id="chart-timeline">
        <Chart
          options={state.options}
          series={state.series}
          type="area"
          height={300}
        />
      </div>
    </div>
  );
};

export default OverviewChart;
